import { useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  ParticipantConfig,
  ParticipantSlot,
} from "@/features/products/products-api";

// The Individual default — mirrors the server's INDIVIDUAL_PARTICIPANT_CONFIG.
const INDIVIDUAL: ParticipantConfig = {
  purchaser_is_participant: true,
  slots: [{ key: "self", label: "Client", min: 1, max: 1 }],
};

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const KEY_RE = /^[a-z0-9_]+$/;

// A single implicit slot for the non-"multiple participants" case.
const singleSlot = (forSomeoneElse: boolean, label?: string): ParticipantSlot =>
  forSomeoneElse
    ? { key: "recipient", label: label || "Recipient", min: 1, max: 1 }
    : { key: "self", label: label || "Client", min: 1, max: 1 };

/**
 * Configures a product's participant/ownership model as two friendly toggles
 * plus a slot editor. Fully controlled: derives its display from `value` and
 * emits a normalized ParticipantConfig via `onChange`. Reports slot-key validity
 * via `onValidityChange` so the parent can gate Save.
 */
export default function ParticipantConfigField({
  value,
  onChange,
  onValidityChange,
}: {
  value?: ParticipantConfig | null;
  onChange: (next: ParticipantConfig) => void;
  onValidityChange?: (ok: boolean) => void;
}) {
  const config = value ?? INDIVIDUAL;
  const forSomeoneElse = config.purchaser_is_participant === false;
  const slots = config.slots?.length ? config.slots : INDIVIDUAL.slots;
  const multiple = slots.length > 1 || slots.some((s) => (s.max ?? 1) > 1);

  // Validity: every slot key non-empty, slug-form, and unique.
  const keys = slots.map((s) => s.key);
  const valid =
    keys.every((k) => !!k && KEY_RE.test(k)) &&
    new Set(keys).size === keys.length;

  useEffect(() => {
    onValidityChange?.(valid);
  }, [valid, onValidityChange]);

  const emit = (next: ParticipantConfig) => onChange(next);

  const toggleForSomeoneElse = (on: boolean) => {
    const next: ParticipantConfig = {
      purchaser_is_participant: !on,
      slots: multiple ? slots : [singleSlot(on, slots[0]?.label)],
    };
    emit(next);
  };

  const toggleMultiple = (on: boolean) => {
    if (on) {
      const seeded =
        slots.length > 1
          ? slots
          : [
              { key: "primary", label: "Primary", min: 1, max: 1 },
              { key: "partner", label: "Partner", min: 1, max: 1 },
            ];
      emit({ ...config, slots: seeded });
    } else {
      emit({ ...config, slots: [singleSlot(forSomeoneElse, slots[0]?.label)] });
    }
  };

  const patchSlot = (i: number, patch: Partial<ParticipantSlot>) => {
    const nextSlots = slots.map((s, idx) => {
      if (idx !== i) return s;
      const merged = { ...s, ...patch };
      // Auto-derive key from label while the key is still the label's slug
      // (i.e. hasn't been manually edited) or empty.
      if (patch.label !== undefined && (!s.key || s.key === slugify(s.label))) {
        merged.key = slugify(patch.label);
      }
      return merged;
    });
    emit({ ...config, slots: nextSlots });
  };

  const addSlot = () => {
    const existing = new Set(slots.map((s) => s.key));
    let n = slots.length + 1;
    let key = `participant_${n}`;
    while (existing.has(key)) key = `participant_${++n}`;
    emit({
      ...config,
      slots: [...slots, { key, label: `Participant ${slots.length + 1}`, min: 1, max: 1 }],
    });
  };

  const removeSlot = (i: number) =>
    emit({ ...config, slots: slots.filter((_, idx) => idx !== i) });

  const summary = (() => {
    if (!multiple)
      return forSomeoneElse ? "Gift / on-behalf" : "Individual";
    if (slots.length === 1) return "Group";
    return `${forSomeoneElse ? "Gift · " : ""}${slots.length} participants`;
  })();

  return (
    <div className="space-y-3 rounded-md border border-gray-200 p-3 bg-white">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-gray-600">Participant model</p>
        <Badge variant="outline">{summary}</Badge>
      </div>

      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm">Purchased for someone else</p>
          <p className="text-[11px] text-gray-400">
            Buyer pays but isn't a participant (gift / corporate).
          </p>
        </div>
        <Switch checked={forSomeoneElse} onCheckedChange={toggleForSomeoneElse} />
      </div>

      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm">Allows multiple participants</p>
          <p className="text-[11px] text-gray-400">e.g. couples or group.</p>
        </div>
        <Switch checked={multiple} onCheckedChange={toggleMultiple} />
      </div>

      {!multiple ? (
        <div>
          <p className="text-[11px] text-gray-500 mb-1">
            Participant label{" "}
            <span className="text-gray-400">(key: {slots[0]?.key})</span>
          </p>
          <Input
            className="h-8 text-sm"
            value={slots[0]?.label ?? ""}
            onChange={(e) => patchSlot(0, { label: e.target.value })}
            placeholder={forSomeoneElse ? "Recipient" : "Client"}
          />
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-gray-500">
              Participant slots{" "}
              <span className="text-gray-400">
                (key is referenced by bundle session mappings — avoid changing
                later)
              </span>
            </p>
            <Button type="button" size="sm" variant="outline" onClick={addSlot}>
              <Plus className="h-4 w-4 mr-1" /> Add slot
            </Button>
          </div>
          {slots.map((s, i) => {
            const dupe = keys.filter((k) => k === s.key).length > 1;
            const badKey = !s.key || !KEY_RE.test(s.key) || dupe;
            return (
              <div
                key={i}
                className="grid grid-cols-[1fr_1fr_auto] items-end gap-2"
              >
                <div>
                  <p className="text-[11px] text-gray-500 mb-1">Label</p>
                  <Input
                    className="h-8 text-sm"
                    value={s.label}
                    onChange={(e) => patchSlot(i, { label: e.target.value })}
                    placeholder="Partner"
                  />
                </div>
                <div>
                  <p className="text-[11px] text-gray-500 mb-1">Key</p>
                  <Input
                    className={`h-8 text-sm ${badKey ? "border-red-400" : ""}`}
                    value={s.key}
                    onChange={(e) => patchSlot(i, { key: e.target.value })}
                    placeholder="partner"
                  />
                </div>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  disabled={slots.length <= 1}
                  onClick={() => removeSlot(i)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            );
          })}
          {!valid && (
            <p className="text-[11px] text-red-500">
              Slot keys must be unique, lowercase, and use letters, numbers or
              underscores.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
